import React from "react";
import type { GroupActivityFields } from "@/main/common/GroupActivityTypes";
import { BannedUserDTO, UserProfileDTO } from "@/generated-client";
import { GroupGovernanceBootstrap, GroupLeaderQueueCounts } from "@/main/react/group/GroupGovernanceTypes";
import type { GroupDiscussionSubmissionPolicyValue } from "@/main/react/group/GroupDiscussionSubmissionPolicy";
import { Localizer } from "@/main/react/Types";
import type { GroupLeadershipLevel } from "@/main/common/GroupGovernanceApi";
import { ArrowUp, CalendarDays, ChartColumn, Clock, Flag, Flame, History, Inbox, ListPlus, Mail, MessageSquareText, PlusSquare, Settings, ShieldCheck, UserMinus, UserPlus, Users, X } from "lucide-react";
import type { BannedUserWithActorAvatar } from "@/main/common/UserAvatarContracts";

export type GroupPageProps = {
    initialSection?: string;
    groupPublicId?: string;
    groupSlug?: string;
    initialName?: string;
    initialDescription?: string | null;
    initialThumbnailUrl?: string | null;
    initialBannerUrl?: string | null;
    mainLocale?: string | null;
    initialDiscussionSubmissionPolicy?: GroupDiscussionSubmissionPolicyValue | null;
    initialCreatorUsername?: string | null;
    initialCreatorProfileImageUrl?: string | null;
    initialCreatedAt?: string | null;
    /** Server-decided (honest community size); read from the page's data attributes, never derived. */
    initialActivity?: GroupActivityFields | null;
    initialDiscussionsCount?: number | null;
    initiallyMember?: boolean;
    initiallyLeader?: boolean;
    /**
     * The viewer's own leadership level in this group ("OWNER" | "ADMINISTRATOR" | "MODERATOR"),
     * or undefined when the viewer is not a leader. Used to gate admin-only affordances (edit
     * settings, invite) by level rather than by the coarse `initiallyLeader` flag.
     */
    viewerLeadershipLevel?: GroupLeadershipLevel;
    initiallyRequested?: boolean;
    initiallyBanned?: boolean;
    privateAccess?: boolean;
    sensitive?: boolean;
    banned?: BannedUserWithActorAvatar | null;
    deniedAccess?: boolean;
    initialGovernanceBootstrap?: GroupGovernanceBootstrap;
    localizer: Localizer;
};

/**
 * P2-A information architecture.
 *
 * The page is described by ONE flat `section` string that also is the URL tail, in one of two
 * shapes:
 *   - a member tab:      "discussions" | "members" | "about"
 *   - a console tool:    "manage/<tool>"
 *
 * Keeping the console under a prefix (rather than a parallel `mode` flag) means routing, leader
 * gating and URL building each stay a single predicate, on both the server and the client.
 */
export const GROUP_MANAGE_PREFIX = "manage/";
export const GROUP_DEFAULT_SECTION = "discussions";

export type GroupMemberTabKey = "discussions" | "members" | "about";

export type GroupPageSelectOption = {
    key: string;
    value: string;
    label: string;
    icon?: React.ElementType;
    /**
     * A count rendered as a pill after the label (`Join requests · 2`).
     *
     * <p><b>Read the note on `getGroupMemberTabs` before adding one to a MEMBER tab.</b> On the
     * member strip a count that describes the GROUP (how busy a room is) is deliberately absent;
     * there, this field is only for a count that describes THE VIEWER'S own unread state. The two
     * look identical on screen and are opposite in what they leak. No member tab uses it today.
     *
     * <p>That objection is about a number shown to people who cannot act on it, and it does not carry
     * over to the manage console: those tabs exist only for leaders, the counts are gated to leaders
     * in SQL, and the whole point of the tool is the size of its queue. So `getGroupManageTools` uses
     * this slot for genuine queue sizes — see
     * designs/01-now/2026-08-03-group-console-pending-work-signals-design.md §3.5.
     *
     * <p>Zero or negative renders nothing at all — never a `0` pill. That is what makes the ABSENCE
     * of a badge readable as "this queue is empty" without opening it.
     */
    badgeCount?: number;
    /**
     * Screen-reader text for `badgeCount`, already localized.
     *
     * <p>The visible pill is `aria-hidden`, because "Join requests 3" read aloud is ambiguous between
     * a count and part of the name. This carries the meaning instead ("3 waiting for you"). Localized
     * by the caller rather than here so this module stays free of a `Localizer` dependency.
     */
    badgeSrLabel?: string;
    /**
     * Stable browser-test hook for the option row, forwarded to `SimpleSelect`'s `SelectItem`.
     * The ordering options carry `group-discussion-ordering-option-<key>`, mirroring the series
     * select's `series-discussion-ordering-option-<key>`.
     */
    dataPlaywrightId?: string;
};

/**
 * The `ordering` URL parameter, and the `n` query parameter of `/ajax/page/group/discussions`.
 *
 * <p>`top` is the group-local score over ALL time; `top-day` / `top-week` / `top-month` are the same
 * score over a rolling 24h / 7d / 30d window. They are four values on one parameter rather than an
 * `ordering` + `range` pair (which is how the home feed does it) because a window is meaningful for
 * exactly one of these sorts — a second parameter would be legal in one of four states and would cost
 * a URL dimension, a history-backed field and a restoration-key component to say so.
 *
 * <p>Backend counterpart: `DiscussionRepositoryCustom.GroupDiscussionOrder.fromOrderingStyle`, which
 * maps these exact strings. An unknown value falls back to `active` here and to `added` there; both
 * fallbacks predate the windows and are unchanged.
 *
 * <p>Design: `designs/02-next/2026-04-21-top-in-group-ordering-design.md` (Phase 2 of section 16).
 */
export type GroupDiscussionOrderingStyle =
    | "added"
    | "active"
    | "top"
    | "top-day"
    | "top-week"
    | "top-month"
    | "new";

/**
 * `sensitive` rides along (round 39 `1d`, D18) so the header byline and the Leaders chip can mask a
 * sensitive leader's photo the way the Members card does. Optional: the SSR bootstrap does not emit it
 * yet, and absent reads as not sensitive.
 */
export type GroupLeaderIdentity = Pick<UserProfileDTO, "username" | "profileImageUrl" | "sensitive"> & {
    leadershipLevel?: GroupLeadershipLevel;
};

export function getGroupLeadership(
    leaders?: GroupLeaderIdentity[] | null,
    creatorUsername?: string | null,
    creatorProfileImageUrl?: string | null,
): GroupLeaderIdentity[] {
    const normalizedLeaders = Array.isArray(leaders) ? leaders : [];
    const distinctLeaders = new Map<string, GroupLeaderIdentity>();

    normalizedLeaders.forEach((leader) => {
        const username = leader?.username?.trim();
        if (!username || distinctLeaders.has(username)) return;
        distinctLeaders.set(username, leader);
    });

    const creator = creatorUsername?.trim();
    if (!creator) {
        return Array.from(distinctLeaders.values());
    }

    const creatorLeader = distinctLeaders.get(creator) ?? {
        username: creator,
        profileImageUrl: creatorProfileImageUrl ?? undefined,
    };
    const remainingLeaders = Array.from(distinctLeaders.entries())
        .filter(([username]) => username !== creator)
        .map(([, leader]) => leader);

    return [creatorLeader, ...remainingLeaders];
}

/**
 * Member-facing tabs. Everyone who can see the group sees exactly these three.
 *
 * <p><b>There is no conversation tab any more</b> (group-chat repositioning, design §5.2). The board
 * tab, its `Board · N` unread pill and the per-page-load fetch that supplied the number are all gone:
 * the group's conversation lives in Messages, and the group page keeps one control pointing at it
 * (`Open chat`, rendered by `GroupTabBar`). What was measured is the reason — 1,867 requests over 8
 * logged days to draw a badge that had been zero for all but five people, ever.
 *
 * <p><b>What was learned here still binds whatever comes next.</b> A count on a member tab may only
 * ever describe THE VIEWER'S own unread state, never the group's activity: a group-level number is a
 * small information leak, and in a quiet group it advertises that the room is dead, so it works
 * against you exactly when you need it most. That objection does not carry over to the manage console
 * — see `getGroupManageTools`.
 *
 * <p><b>A fourth tab is not free.</b> The mobile strip's budget was spent once at three tabs and
 * again at four; see the geometry note in `GroupPageHeaders.tsx` and the 390px assertion in
 * `group-page-layout-invariants.spec.mjs`, which iterates whatever tabs exist rather than hardcoding
 * a count — so adding one here fails that spec rather than clipping a label silently. Dropping back
 * to three is what pays for the `Open chat` pill in the trailing slot.
 */
export function getGroupMemberTabs(localizer: Localizer, options?: {
    deniedAccess?: boolean;
}): GroupPageSelectOption[] {
    if (options?.deniedAccess) {
        return [];
    }

    return [
        { key: "discussions", value: "discussions", label: localizer("group.section.discussions"), icon: MessageSquareText },
        { key: "members", value: "members", label: localizer("group.section.members"), icon: Users },
        // About carries the governance rail content AND the full leaders list; "leaders" is no longer
        // a route of its own (GroupController redirects the old path here).
        { key: "about", value: "about", label: localizer("group.tabs.about"), icon: ShieldCheck },
    ];
}

/**
 * Moderator-console tools. `value` is the full section string, so a tool is routed, gated and
 * URL-built by exactly the same code path as a member tab.
 *
 * `metrics` is the one entry that is a separate PAGE rather than a console panel (P1-C:
 * GroupController hands /group/{slug}/metrics to its own template), so it keeps its bare value and
 * is navigated to rather than switched to.
 */
export function getGroupManageTools(localizer: Localizer, options?: {
    initiallyLeader?: boolean;
    canManageInvitations?: boolean;
    canEditSettings?: boolean;
    /**
     * The four queue sizes, or null when they are unknown (not a leader, or an AJAX-only render with
     * no bootstrap). Null renders no badge anywhere — never a row of zeros.
     */
    queueCounts?: GroupLeaderQueueCounts | null;
}): GroupPageSelectOption[] {
    if (!options?.initiallyLeader) {
        return [];
    }

    const counts = options?.queueCounts ?? null;
    // `Waiting for you` for the two queues that hold a decision the LEADER owes; `In this list` for
    // the two that do not. A pending invitation is waiting on the invitee and the ban list is a
    // roster, so promising a leader that either is "waiting for you" sends them to a screen with
    // nothing to do — the raised defect, inverted. See
    // designs/01-now/2026-08-03-group-console-pending-work-signals-design.md §3.5.
    const awaitingBadge = (count?: number) => (count && count > 0
        ? { badgeCount: count, badgeSrLabel: localizer("group.manage.tab.awaiting", String(count)) }
        : {});
    const listedBadge = (count?: number) => (count && count > 0
        ? { badgeCount: count, badgeSrLabel: localizer("group.manage.tab.listed", String(count)) }
        : {});

    return [
        // The announcements tool that led this list was retired on 2026-09-21 (group chat Phase 3):
        // a pinned chat message is what a leader's "read this first" became.
        { key: "requests", value: "manage/requests", label: localizer("group.section.requests"), icon: Inbox, ...awaitingBadge(counts?.joinRequests) },
        { key: "submissions", value: "manage/submissions", label: localizer("group.section.discussion_submissions"), icon: ListPlus, ...awaitingBadge(counts?.discussionSubmissions) },
        // Invitation management is privacy-aware: administrators/owners always, plus moderators in
        // private groups. Backed by GroupService.assertViewerCanManageInvitations.
        ...(options?.canManageInvitations ? [
            { key: "invitations", value: "manage/invitations", label: localizer("group.section.invitations"), icon: Mail, ...listedBadge(counts?.invitations) },
            // The fifth queue (2026-09-02): members asking a leader to invite somebody into a private
            // group. Same `canManageInvitations` gate as the row above, because the service bar for
            // resolving one is the same MODERATOR+ that governs sending the invitation it produces.
            //
            // `awaitingBadge`, not `listedBadge`: these are pending work addressed TO the leader, like
            // join requests and submissions, rather than a list of things already sent.
            { key: "inviteRequests", value: "manage/invite-requests", label: localizer("group.section.invite_requests"), icon: UserPlus, ...awaitingBadge(counts?.inviteRequests) },
        ] : []),
        { key: "banned", value: "manage/banned", label: localizer("group.moderation.banned.title"), icon: UserMinus, ...listedBadge(counts?.bannedMembers) },
        { key: "excluded", value: "manage/excluded", label: localizer("group.moderation.excluded.title"), icon: X },
        // Group chat moderation toolkit (design 2026-08-28): C5 reports, C2 timeouts, C3 history.
        //
        // ⚠️ THIS LIST IS THE REACHABILITY GATE, not just the tab strip. `GroupPage` builds
        // `allowedSections` from it, and `normalizeSection` rewrites any manage URL that is not in
        // that set to `defaultManageSection`. So a section missing HERE is not merely undiscoverable
        // — its deep link silently lands on Join requests and its render branch in `GroupPage`
        // becomes dead code. That is exactly what shipped on 2026-08-28: the toolkit added the three
        // sections to a SECOND list — `group/groupSections.ts`, which by then had no production consumer
        // at all — wrote the render branches and the backend endpoints, and left this function alone, so
        // the entire leader-facing half of the feature was unreachable. That decoy list was deleted on
        // 2026-09-04 (review finding `F16`), so THIS function is now the only group-section registry in
        // the frontend. Adding a `manage/*` render branch obliges an entry here, in the same task.
        { key: "reports", value: "manage/reports", label: localizer("group.moderation.toolkit.reports_title"), icon: Flag },
        { key: "timeouts", value: "manage/timeouts", label: localizer("group.moderation.toolkit.timeouts_title"), icon: Clock },
        { key: "history", value: "manage/history", label: localizer("group.moderation.toolkit.history_title"), icon: History },
        // Editing settings needs ADMINISTRATOR or OWNER on the backend, same as the dialog entry.
        ...(options?.canEditSettings ? [
            { key: "settings", value: "manage/settings", label: localizer("group.manage.section.settings"), icon: Settings },
        ] : []),
        { key: "metrics", value: "metrics", label: localizer("group.metrics.title"), icon: ChartColumn },
    ];
}

/**
 * How many items are waiting on a LEADER'S decision, across the whole console — the number the
 * "Manage group" entry pill carries.
 *
 * <p><b>Deliberately narrower than the sum of the four tab badges.</b> Only a join request and a post
 * submission are blocked on the leader: a pending invitation is waiting on the person invited, and
 * the ban list is a roster rather than a queue. A pill that added those in would send a leader into
 * the console to find nothing to do — which is the defect this whole feature exists to remove, only
 * pointing the other way.
 */
export function getGroupPendingLeaderActionCount(queueCounts?: GroupLeaderQueueCounts | null): number {
    if (!queueCounts) {
        return 0;
    }
    return Math.max(0, queueCounts.joinRequests) + Math.max(0, queueCounts.discussionSubmissions);
}

export function isGroupManageSection(section?: string): boolean {
    return Boolean(section && section.startsWith(GROUP_MANAGE_PREFIX));
}

/**
 * Pre-P2-A section values that moved. Applied client-side as well as by the server redirect so a
 * soft navigation (history popstate, a stale in-page link) converges on the canonical URL.
 *
 * <p><b>`board` is deliberately NOT aliased.</b> It did not move to another section — it left the
 * group page entirely for Messages, and there is no group-page destination to send it to. An alias
 * would have to name one, so an unrecognised `board` falls through `normalizeSection` to the first
 * member tab (Posts) and the URL is rewritten to match. A stale bookmark therefore lands on the
 * group rather than on a blank panel, and `Open chat` is beside it.
 */
const LEGACY_SECTION_ALIASES: Record<string, string> = {
    leaders: "about",
    requests: "manage/requests",
    submissions: "manage/submissions",
    invitations: "manage/invitations",
    banned: "manage/banned",
    excluded: "manage/excluded",
};

export function resolveLegacyGroupSection(section?: string): string | undefined {
    if (!section) return section;
    return LEGACY_SECTION_ALIASES[section] ?? section;
}

/** Build the path tail for a section, encoding each segment so "manage/requests" survives intact. */
export function buildGroupSectionPath(groupSlug: string, section: string): string {
    const encodedSlug = encodeURIComponent(groupSlug);
    const encodedSection = section.split("/").map(encodeURIComponent).join("/");
    return `/group/${encodedSlug}${encodedSection ? `/${encodedSection}` : ""}`;
}

export function normalizeGroupDiscussionOrdering(value?: string): GroupDiscussionOrderingStyle {
    switch (value) {
        case "added":
        case "active":
        case "top":
        case "top-day":
        case "top-week":
        case "top-month":
        case "new":
            return value;
        default:
            return "active";
    }
}

export function getGroupDiscussionOrderingOptions(localizer: Localizer): GroupPageSelectOption[] {
    return [
        { key: "added", value: "added", label: localizer("group.discussions.ordering.added"), dataPlaywrightId: "group-discussion-ordering-option-added", icon: PlusSquare },
        { key: "active", value: "active", label: localizer("group.discussions.ordering.active"), dataPlaywrightId: "group-discussion-ordering-option-active", icon: Flame },
        // The four "top" options share one icon deliberately: they are one sort over four time
        // windows, and four different glyphs would read as four different sorts. The label carries
        // the window, which is the only thing that differs.
        { key: "top", value: "top", label: localizer("group.discussions.ordering.top"), dataPlaywrightId: "group-discussion-ordering-option-top", icon: ArrowUp },
        { key: "top-day", value: "top-day", label: localizer("group.discussions.ordering.top-day"), dataPlaywrightId: "group-discussion-ordering-option-top-day", icon: ArrowUp },
        { key: "top-week", value: "top-week", label: localizer("group.discussions.ordering.top-week"), dataPlaywrightId: "group-discussion-ordering-option-top-week", icon: ArrowUp },
        { key: "top-month", value: "top-month", label: localizer("group.discussions.ordering.top-month"), dataPlaywrightId: "group-discussion-ordering-option-top-month", icon: ArrowUp },
        { key: "new", value: "new", label: localizer("group.discussions.ordering.new"), dataPlaywrightId: "group-discussion-ordering-option-new", icon: CalendarDays },
    ];
}

export function InfoCard({
    icon: Icon,
    title,
    subtitle,
    children,
}: {
    icon?: React.ElementType;
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}) {
    return (
        <section className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-3 flex items-start gap-3">
                {Icon ? (
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-text-main">
                        <Icon className="size-4" />
                    </div>
                ) : null}
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-text-main">{title}</div>
                    {subtitle ? <div className="text-xs text-neutral-text">{subtitle}</div> : null}
                </div>
            </div>
            <div>{children}</div>
        </section>
    );
}
